/**
 * ============================================
 * MAIN APP COMPONENT
 * ============================================
 * 
 * Root application component with routing and auth.
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { AlertProvider } from '@/context/AlertContext';
import { ConnectionProvider } from '@/context/ConnectionContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { LevelSettingsProvider } from '@/context/LevelSettingsContext';
import { Dashboard } from '@/components/dashboard';
import { LoginPage } from '@/pages/LoginPage';
import { DisconnectedOverlay } from '@/components/common/DisconnectedOverlay';

// ============================================
// Protected Route Component
// ============================================

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// ============================================
// App Routes
// ============================================

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ============================================
// Main App Component
// ============================================

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <LevelSettingsProvider>
            <ConnectionProvider>
              <AlertProvider>
                <AppRoutes />
                <DisconnectedOverlay />
              </AlertProvider>
            </ConnectionProvider>
          </LevelSettingsProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
